// PitchEditorChart.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend);

export default function PitchEditorChart({
  pitch,
  segment,
  broadcastTimeAbs,
  onSetWordBoundaryAbs,
  onSetLastWordEndAbs,
  onSetSegmentEndAbs,
}) {
  const chartRef = useRef(null);
  const overlayRef = useRef(null);
  const [drag, setDrag] = useState(null);

  const words = segment?.words ?? [];
  const segStart = Number(segment?.start ?? 0);
  const segEnd = Number(segment?.end ?? segStart);
  const segLen = Math.max(1e-6, segEnd - segStart);

  const lastWordEndAbs = useMemo(() => {
    if (!words.length) return segEnd;
    const e = Number(words[words.length - 1]?.end);
    return Number.isFinite(e) ? e : segEnd;
  }, [words, segEnd]);

  const xMin = segStart;
  const xMax = Math.max(segEnd, lastWordEndAbs) + 1e-6;

  // Detect pitch.xs mode (relative vs absolute)
  const xMode = useMemo(() => {
    const xs = pitch?.xs ?? [];
    if (!xs.length) return "absolute";

    const sample = xs.slice(0, 50).map(Number).filter(Number.isFinite);
    if (!sample.length) return "absolute";

    const minX = Math.min(...sample);
    const maxX = Math.max(...sample);

    const looksRelative =
      minX >= -0.05 &&
      maxX <= segLen + 0.25 &&
      Math.abs(minX - segStart) > 0.5;

    return looksRelative ? "relative" : "absolute";
  }, [pitch, segLen, segStart]);

  // Build pitch points (absolute x)
  const { xsAbs, ysClean, yMin, yMax } = useMemo(() => {
    const rawXs = pitch?.xs ?? [];
    const rawYs = pitch?.ys ?? [];

    const pts = [];
    for (let i = 0; i < rawXs.length; i++) {
      const xRaw = Number(rawXs[i]);
      if (!Number.isFinite(xRaw)) continue;

      const xAbs = xMode === "relative" ? segStart + xRaw : xRaw;
      if (xAbs < xMin || xAbs > xMax) continue;

      const f0 = rawYs[i];
      const y =
        f0 == null || !Number.isFinite(Number(f0)) || Number(f0) <= 0
          ? null
          : Number(f0);

      pts.push({ x: xAbs, y });
    }

    const valid = pts.map((p) => p.y).filter((v) => Number.isFinite(v));
    const ymin = valid.length ? Math.min(...valid) : 0;
    const ymax = valid.length ? Math.max(...valid) : 1;

    return {
      xsAbs: pts.map((p) => p.x),
      ysClean: pts.map((p) => p.y),
      yMin: ymin,
      yMax: ymax,
    };
  }, [pitch, xMode, segStart, xMin, xMax]);

  const yRange = Math.max(1e-6, yMax - yMin);

  // Boundaries
  const internalBoundariesAbs = useMemo(() => {
    const out = [];
    for (let i = 0; i < words.length - 1; i++) {
      const t = Number(words[i]?.end);
      if (Number.isFinite(t)) out.push({ index: i, x: t });
    }
    return out;
  }, [words]);

  const lastBoundaryAbs = useMemo(() => {
    if (!words.length) return null;
    const t = Number(words[words.length - 1]?.end);
    if (!Number.isFinite(t)) return null;
    return { x: t };
  }, [words]);

  const cursorAbs = useMemo(() => {
    if (!Number.isFinite(broadcastTimeAbs)) return null;
    const x = Number(broadcastTimeAbs);
    if (x < xMin || x > xMax) return null;
    return x;
  }, [broadcastTimeAbs, xMin, xMax]);

  // Datasets
  const data = useMemo(() => {
    const datasets = [];
    const extend = yRange * 0.15;

    datasets.push({
      label: "Pitch",
      data: xsAbs.map((x, i) => ({ x, y: ysClean[i] })),
      borderColor: "#2563eb",
      borderWidth: 2,
      tension: 0.25,
      pointRadius: 0,
      spanGaps: false,
      showLine: true,
    });

    datasets.push({
      label: "SegStart",
      data: [
        { x: segStart, y: yMin - extend },
        { x: segStart, y: yMax + extend },
      ],
      borderColor: "rgba(34,197,94,0.95)",
      borderWidth: 2,
      pointRadius: 0,
      showLine: true,
    });

    datasets.push({
      label: "SegEnd",
      data: [
        { x: segEnd, y: yMin - extend },
        { x: segEnd, y: yMax + extend },
      ],
      borderColor: "rgba(239,68,68,0.95)",
      borderWidth: 2,
      pointRadius: 0,
      showLine: true,
    });

    for (const b of internalBoundariesAbs) {
      datasets.push({
        label: `WB-${b.index}`,
        data: [
          { x: b.x, y: yMin - extend },
          { x: b.x, y: yMax + extend },
        ],
        borderColor: "rgba(107,114,128,0.55)",
        borderWidth: 1,
        borderDash: [5, 5],
        pointRadius: 0,
        showLine: true,
      });
    }

    if (lastBoundaryAbs) {
      datasets.push({
        label: "WB-LAST",
        data: [
          { x: lastBoundaryAbs.x, y: yMin - extend },
          { x: lastBoundaryAbs.x, y: yMax + extend },
        ],
        borderColor: "rgba(107,114,128,0.7)",
        borderWidth: 1.5,
        borderDash: [5, 5],
        pointRadius: 0,
        showLine: true,
      });
    }

    if (cursorAbs != null) {
      datasets.push({
        label: "Cursor",
        data: [
          { x: cursorAbs, y: yMin - extend },
          { x: cursorAbs, y: yMax + extend },
        ],
        borderColor: "rgba(16,185,129,1)",
        borderWidth: 2,
        pointRadius: 0,
        showLine: true,
      });
    }

    return { datasets };
  }, [
    xsAbs,
    ysClean,
    segStart,
    segEnd,
    internalBoundariesAbs,
    lastBoundaryAbs,
    cursorAbs,
    yMin,
    yMax,
    yRange,
  ]);

  // IMPORTANT: kill datalabel spam even if plugin is globally registered elsewhere
  const options = useMemo(() => {
    const extend = yRange * 0.15;
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      parsing: false,
      normalized: true,
      scales: {
        x: {
          type: "linear",
          min: xMin,
          max: xMax,
          grid: { display: false },
          ticks: { display: true },
          title: { display: true, text: "Time (s) — absolute" },
        },
        y: {
          min: yMin - extend,
          max: yMax + extend,
          grid: { color: "rgba(0,0,0,0.05)" },
          title: { display: true, text: "Pitch (Hz)" },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false }, // <- reduce clutter while dragging
        datalabels: { display: false }, // <- KEY FIX (works even if plugin registered globally)
      },
      interaction: { mode: "nearest", axis: "x", intersect: false },
    };
  }, [xMin, xMax, yMin, yMax, yRange]);

  // Pointer helpers
  const pointerToXAbs = (evt) => {
    const chart = chartRef.current;
    if (!chart) return null;
    const xScale = chart.scales?.x;
    if (!xScale) return null;

    const rect = chart.canvas.getBoundingClientRect();
    const px = evt.clientX - rect.left;
    const xVal = xScale.getValueForPixel(px);
    if (!Number.isFinite(xVal)) return null;
    return xVal;
  };

  const findNearestDraggable = (xAbs, tolerancePx = 12) => {
    const chart = chartRef.current;
    if (!chart) return null;
    const xScale = chart.scales?.x;
    if (!xScale) return null;

    const xPx = xScale.getPixelForValue(xAbs);
    let best = null;

    for (const b of internalBoundariesAbs) {
      const bPx = xScale.getPixelForValue(b.x);
      const d = Math.abs(bPx - xPx);
      if (d <= tolerancePx && (!best || d < best.d)) best = { type: "wordBoundary", index: b.index, d };
    }

    if (lastBoundaryAbs && onSetLastWordEndAbs) {
      const bPx = xScale.getPixelForValue(lastBoundaryAbs.x);
      const d = Math.abs(bPx - xPx);
      if (d <= tolerancePx && (!best || d < best.d)) best = { type: "lastEnd", d };
    }

    if (onSetSegmentEndAbs) {
      const endPx = xScale.getPixelForValue(segEnd);
      const d = Math.abs(endPx - xPx);
      if (d <= tolerancePx && (!best || d < best.d)) best = { type: "segEnd", d };
    }

    return best ? { type: best.type, index: best.index } : null;
  };

  const onPointerDown = (evt) => {
    const xAbs = pointerToXAbs(evt);
    if (xAbs == null) return;
    const target = findNearestDraggable(xAbs, 12);
    if (!target) return;

    overlayRef.current?.setPointerCapture?.(evt.pointerId);
    setDrag({ ...target, pointerId: evt.pointerId });
  };

  const onPointerMove = (evt) => {
    if (!drag) return;
    let xAbs = pointerToXAbs(evt);
    if (xAbs == null) return;

    if (drag.type === "wordBoundary") {
      xAbs = Math.max(segStart, Math.min(segEnd, xAbs));
      onSetWordBoundaryAbs?.(drag.index, xAbs);
      return;
    }
    if (drag.type === "lastEnd") {
      xAbs = Math.max(segStart, xAbs);
      onSetLastWordEndAbs?.(xAbs);
      return;
    }
    if (drag.type === "segEnd") {
      xAbs = Math.max(segStart, xAbs);
      onSetSegmentEndAbs?.(xAbs);
      return;
    }
  };

  const onPointerUp = () => {
    if (!drag) return;
    try {
      overlayRef.current?.releasePointerCapture?.(drag.pointerId);
    } catch {}
    setDrag(null);
  };

  useEffect(() => {
    return () => setDrag(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segment?.id]);

  if (!segment || !pitch?.xs?.length) return null;

  return (
    <div className="w-full min-w-0 bg-white rounded-xl border border-gray-200 p-2">
      <div className="text-xs text-gray-500 mb-2">
        Drag dashed boundaries • Green/Red are segment bounds • X axis absolute • xsMode: {xMode}
      </div>

      <div className="relative w-full h-56">
        <Line ref={chartRef} data={data} options={options} />
        <div
          ref={overlayRef}
          className="absolute inset-0"
          style={{ touchAction: "none", cursor: drag ? "ew-resize" : "default" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
      </div>
    </div>
  );
}